import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type GooglePay from '@adyen/adyen-web/dist/types/components/GooglePay/GooglePay';
import type UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import type AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError';
import { useApolloClient } from '@apollo/client';
import { countryCodeMapper, currencyMapper, storeKeyMapper } from '@shared/utils/src/ct';
import { useCartContext, useLocaleContext, useStaticContext } from '../../context';
import type { PaymentContextType } from '../../context';
import type { GetCartQuery } from '../../graphqlDocument/cart/getCart.generated';
import { GetCartDocument } from '../../graphqlDocument/cart/getCart.generated';
import { usePlaceOrderMutation } from '../../graphqlDocument/order/placeOrder.generated';
import { validateCartItemsStock } from '../../services';
import type { PaymentResponseType, PlaceOrderMutationFn } from '../../types';
import { getCtLocale } from '../../utils';
import { isHomeDeliveryMethod } from '../../utils/deliveryUtils';
import { newRelicAvailabilityChecker } from '../../utils/newrelic';
import { useCartValidation } from '../useCartValidation';
import { useDidMount } from '../useDidMount';
import { useNRBASession } from '../useNRBASession';
import { createPaymentMethodsConfig } from './config';
import type { StateStatus } from './utils';
import { createTranslationsObject, formatAddress, handleChange, handleError, handlePaymentCompleted } from './utils';
import type { FormatAddress } from './utils/formatAddress';

const clientKey = process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY ?? '';

export interface CartAddress {
  billingAddress?: FormatAddress;
  deliveryAddress?: FormatAddress;
}

export function useAdyenConfig(paymentContext: PaymentContextType) {
  const router = useRouter();
  const { locale } = useLocaleContext();
  const { baseUrl } = useStaticContext();
  const { basketTotalCent, cartId, anonymousId, miniCart } = useCartContext();
  const { isDigitalCart } = miniCart;
  const nrbaContextHeader = useNRBASession();
  const { validateDiscountsBeforePayment } = useCartValidation();
  const {
    paymentData,
    setAdyenComponent,
    setIsPaymentBtnDisabled,
    customerPaymentMethodType,
    setPaymentError,
    setAvailableAdyenComponents,
    setValidityStatus,
    setCheckedStatus,
    setAdyenCheckout,
    setGiftCardOrder,
    setCanPayWithGiftCards,
    setCustomerPaymentMethodType,
    isCSA,
    setOnErrorGiftCard,
    setIsErrorGiftCardInput,
    setGiftCardStatus,
    setIsGiftCardEnough,
    isGiftCardEnough,
    isGiftCardRemoved,
    setPaymentInProgress,
    setIsIframeError,
    setIsDialogOpen,
  } = paymentContext;
  const apolloClient = useApolloClient();
  const isMounted = useDidMount();
  const { t } = useTranslation(['lib-global-common']);

  const [placeOrder] = usePlaceOrderMutation({
    ...nrbaContextHeader,
    onError: (error) => {
      console.error('Error placing order:', error);
      newRelicAvailabilityChecker(() => {
        window.newrelic.noticeError(new Error(`Error placing order: ${error}`));
      });
    },
  });

  const translations = createTranslationsObject(locale);

  const getCartAddresses = useCallback(async () => {
    const { data: cart } = await apolloClient.query<GetCartQuery>({
      query: GetCartDocument,
      variables: {
        input: {
          anonymousId,
          cartId,
          locale: getCtLocale(locale),
          storeKey: storeKeyMapper(locale),
        },
      },
      ...nrbaContextHeader,
    });

    return {
      cartBillingAddress: formatAddress(cart.getCart?.billingAddress),
      // set delivery address only in case of home delivery
      cartDeliveryAddress: isHomeDeliveryMethod(cart.getCart?.deliveryMethod)
        ? formatAddress(cart.getCart?.deliveryAddress)
        : null,
    };
  }, [anonymousId, apolloClient, cartId, locale, nrbaContextHeader]);

  const resolveData = useCallback(
    async (data: CartAddress) => {
      const { cartBillingAddress, cartDeliveryAddress } = await getCartAddresses();
      const result = { ...data };

      if (cartBillingAddress) {
        result.billingAddress = cartBillingAddress;
      }
      /* When the cart only contains digital products such as an e-gift card that does not require physical shipping, the
       * customer won’t be able to input a delivery address at checkout. The delivery address is then set as the same
       * value as the billing address when the cart is only digital, else it is set as the delivery address input by the
       * customer at checkout. */
      if (isDigitalCart && cartBillingAddress) {
        result.deliveryAddress = cartBillingAddress;
      } else if (cartDeliveryAddress) {
        result.deliveryAddress = cartDeliveryAddress;
      }

      return result;
    },
    [getCartAddresses, isDigitalCart],
  );

  const placeOrderMutation: PlaceOrderMutationFn = useCallback(async () => {
    try {
      const result = await placeOrder({
        variables: {
          input: {
            anonymousId,
            cartId,
            storeKey: storeKeyMapper(locale),
          },
        },
      });

      if (!result?.data?.placeOrder.accepted) {
        console.error('Order was not accepted.');
      }
      return result;
    } catch (error) {
      console.error('Error occurred during placeOrderMutation:', error);
      newRelicAvailabilityChecker(() => {
        window.newrelic.noticeError(new Error(`Error occurred during placeOrderMutation: ${error}`));
      });
      return null; // Return null instead of throwing
    }
  }, [anonymousId, cartId, locale, placeOrder]);

  const onBeforeSubmit = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (data: any, actions: any) => {
      setIsPaymentBtnDisabled(true);
      await validateCartItemsStock({
        locale,
        apolloClient,
        input: {
          cartId,
          locale: getCtLocale(locale),
        },
        nrbaContextHeader,
        onSuccess: async () => {
          actions.resolve(await resolveData(data));
        },
      });
    },
    [apolloClient, cartId, locale, setIsPaymentBtnDisabled, nrbaContextHeader, resolveData],
  );

  useEffect(() => {
    if (!isMounted || !paymentData || isCSA) return;

    const createCheckout = async () => {
      const AdyenCheckout = (await import('@adyen/adyen-web')).default;

      const checkout = await AdyenCheckout({
        clientKey,
        locale,
        countryCode: countryCodeMapper(locale),
        environment: clientKey.split('_')[0],
        translations,
        analytics: {
          enabled: true,
        },
        session: {
          id: paymentData.session.id,
          sessionData: paymentData.session.sessionData,
        },
        amount: {
          currency: currencyMapper[locale],
          value: basketTotalCent,
        },
        allowPaymentMethods: paymentData.paymentMethods.map(({ type }) => type),
        paymentMethodsConfiguration: createPaymentMethodsConfig({
          locale,
          miniCart,
          cartId,
          baseUrl,
          isGiftCardEnough,
          isGiftCardRemoved,
          paymentData,
          setIsPaymentBtnDisabled,
          setPaymentInProgress,
          setIsDialogOpen,
          setIsGiftCardEnough,
          setIsErrorGiftCardInput,
          setCanPayWithGiftCards,
          setOnErrorGiftCard,
          setPaymentError,
          setGiftCardOrder,
          getCartAddresses,
          placeOrderMutation,
          resolveData,
          validateDiscountsBeforePayment,
          onBeforeSubmit,
          bancontactMobileConfig: {
            introduction: t('checkout.introduction.text.scan.QR'),
          },
        }),
        onError: (error: AdyenCheckoutError) => {
          handleError({
            error,
            cartId,
            locale,
            paymentData,
            customerPaymentMethodType,
            isGiftCardEnough,
            isGiftCardRemoved,
            setIsIframeError,
            setIsErrorGiftCardInput,
            setOnErrorGiftCard,
          });
        },
        onChange: (state: StateStatus) => {
          handleChange({
            state,
            setValidityStatus,
            setCheckedStatus,
            setIsPaymentBtnDisabled,
            setIsErrorGiftCardInput,
            setGiftCardStatus,
            setPaymentError,
            setCustomerPaymentMethodType,
          });
        },
        onPaymentCompleted: async (result: PaymentResponseType, _: UIElement) => {
          handlePaymentCompleted({
            router,
            cartId,
            locale,
            result,
            paymentData,
            setPaymentError,
            setPaymentInProgress,
            setIsPaymentBtnDisabled,
          });
        },
      });

      // This code checks which payment methods from Adyen are actually available, passed forward through the paymentContext to the UI components to mount particular payment methods
      const components = await Promise.all(
        paymentData.paymentMethods.map(async ({ type }) => {
          const component = checkout.create(type);

          let isAvailable = true;
          // Google Pay, Apple Pay and Gift Cards components are not available in all countries and under all circumstances
          if ((component as GooglePay).isAvailable) {
            try {
              isAvailable = await (component as GooglePay).isAvailable();
            } catch {
              isAvailable = false;
            }
          }
          component.remove();
          return { [type]: !!isAvailable };
        }),
      );

      const availableComponents = components
        // Filtering out any null/undefined values from the components array
        .filter((component): component is { [method: string]: boolean } => !!component)
        // Merging all remaining objects into a single object
        .reduce((allComponents, component) => ({ ...allComponents, ...component }), {});

      setAdyenCheckout(checkout);
      setAvailableAdyenComponents(availableComponents);
    };

    createCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketTotalCent, isMounted, locale, paymentData, setAdyenComponent, setIsPaymentBtnDisabled, isCSA]);
}
